import { locales } from "@/config/locale";

type MetadataEntry = {
	key?: string | null;
	value?: string | null;
};

export type ProductUxFaqItem = {
	question: string;
	answer: string;
};

export type ProductUxMarketCopy = {
	locale: string;
	title: string;
	shortDescription: string;
	complianceNote: string;
	longDescription: string | null;
	seoTitle: string | null;
	seoDescription: string | null;
};

/**
 * V2 market copy: locale-aware, includes all translatable UX fields.
 * longDescription excluded — use Saleor native product.translation.description.
 */
export type ProductUxMarketCopyV2 = {
	/** Explicit opt-in to an authored product-copy profile; absent for legacy metadata. */
	copyVersion?: string;
	title: string;
	shortDescription: string;
	complianceNote: string;
	seoTitle: string | null;
	seoDescription: string | null;
	keyBenefits: string[];
	usageContext: string | null;
	safetySummary: string | null;
	faq: ProductUxFaqItem[];
	shippingNote: string | null;
};

export const RESEARCH_COMMERCE_COPY_VERSION = "research-commerce-2026-10-20";
export const ADDITIVE_LOCALE_COPY_VERSION = "new-locale-additive-2026-10-21";

/** Only these reviewed import profiles own the PDP short description and FAQ priority. */
export function isAuthoredCopyProfile(value: unknown): boolean {
	return value === RESEARCH_COMMERCE_COPY_VERSION || value === ADDITIVE_LOCALE_COPY_VERSION;
}

export type ProductUxModel = {
	shortDescription: string | null;
	longDescription: string | null;
	usageContext: string | null;
	shippingNote: string | null;
	safetySummary: string | null;
	keyBenefits: string[];
	trustBadges: string[];
	/** Show "CoA enthalten" badge when true (from ux_has_coa metadata) */
	hasCoA: boolean;
	/** Show "HPLC-verifiziert" badge when true (from ux_lab_verified metadata) */
	labVerified: boolean;
	faq: ProductUxFaqItem[];
	marketCopyByChannel: Record<string, ProductUxMarketCopy>;
	selectedMarketCopy: ProductUxMarketCopy | null;
	/** V2: locale-aware market copy selected by channel+locale. Null when no translation exists. */
	localizedMarketCopy: ProductUxMarketCopyV2 | null;
};

const emptyUxModel: ProductUxModel = {
	shortDescription: null,
	longDescription: null,
	usageContext: null,
	shippingNote: null,
	safetySummary: null,
	keyBenefits: [],
	trustBadges: [],
	hasCoA: false,
	labVerified: false,
	faq: [],
	marketCopyByChannel: {},
	selectedMarketCopy: null,
	localizedMarketCopy: null,
};

function parseJsonArray(value: string | null | undefined): unknown[] {
	if (!value) return [];
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

function parseJsonObject(value: string | null | undefined): Record<string, unknown> {
	if (!value) return {};
	try {
		const parsed = JSON.parse(value);
		return parsed && typeof parsed === "object" && !Array.isArray(parsed)
			? (parsed as Record<string, unknown>)
			: {};
	} catch {
		return {};
	}
}

function asStringArray(value: unknown[]): string[] {
	return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

export function parseFaqJson(json: string | null | undefined): ProductUxFaqItem[] {
	if (!json) return [];
	try {
		const parsed = JSON.parse(json);
		return Array.isArray(parsed) ? asFaqItems(parsed) : [];
	} catch {
		return [];
	}
}

function asFaqItems(value: unknown[]): ProductUxFaqItem[] {
	return value
		.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object")
		.map((item) => {
			// Support both full ("question"/"answer") and short ("q"/"a") formats
			const question =
				typeof item.question === "string" ? item.question : typeof item.q === "string" ? item.q : null;
			const answer =
				typeof item.answer === "string" ? item.answer : typeof item.a === "string" ? item.a : null;
			return question && answer ? { question, answer } : null;
		})
		.filter((item): item is ProductUxFaqItem => item !== null);
}

function asMarketCopyMap(value: Record<string, unknown>): Record<string, ProductUxMarketCopy> {
	const result: Record<string, ProductUxMarketCopy> = {};
	for (const [channel, item] of Object.entries(value)) {
		if (!item || typeof item !== "object") continue;
		const locale = (item as { locale?: unknown }).locale;
		const title = (item as { title?: unknown }).title;
		const shortDescription = (item as { short_description?: unknown; shortDescription?: unknown })
			.short_description;
		const shortDescriptionCamel = (item as { short_description?: unknown; shortDescription?: unknown })
			.shortDescription;
		const complianceNote = (item as { compliance_note?: unknown; complianceNote?: unknown }).compliance_note;
		const complianceNoteCamel = (item as { compliance_note?: unknown; complianceNote?: unknown })
			.complianceNote;
		const longDescription = (item as { long_description?: unknown; longDescription?: unknown })
			.long_description;
		const longDescriptionCamel = (item as { long_description?: unknown; longDescription?: unknown })
			.longDescription;
		const seoTitle = (item as { seo_title?: unknown; seoTitle?: unknown }).seo_title;
		const seoTitleCamel = (item as { seo_title?: unknown; seoTitle?: unknown }).seoTitle;
		const seoDescription = (item as { seo_description?: unknown; seoDescription?: unknown }).seo_description;
		const seoDescriptionCamel = (item as { seo_description?: unknown; seoDescription?: unknown })
			.seoDescription;

		if (
			typeof locale !== "string" ||
			typeof title !== "string" ||
			(typeof shortDescription !== "string" && typeof shortDescriptionCamel !== "string") ||
			(typeof complianceNote !== "string" && typeof complianceNoteCamel !== "string")
		) {
			continue;
		}

		result[channel] = {
			locale,
			title,
			shortDescription: (shortDescription ?? shortDescriptionCamel) as string,
			complianceNote: (complianceNote ?? complianceNoteCamel) as string,
			longDescription:
				typeof longDescription === "string"
					? longDescription
					: typeof longDescriptionCamel === "string"
						? longDescriptionCamel
						: null,
			seoTitle:
				typeof seoTitle === "string" ? seoTitle : typeof seoTitleCamel === "string" ? seoTitleCamel : null,
			seoDescription:
				typeof seoDescription === "string"
					? seoDescription
					: typeof seoDescriptionCamel === "string"
						? seoDescriptionCamel
						: null,
		};
	}
	return result;
}

// Locale codes used for V2 format detection (must match storefront locales).
const KNOWN_LOCALES = new Set<string>(locales);

/** Detect whether a channel entry is V2 (locale-keyed) or V1 (flat with `locale` field). */
function isV2ChannelEntry(entry: unknown): entry is Record<string, unknown> {
	if (!entry || typeof entry !== "object" || Array.isArray(entry)) return false;
	const keys = Object.keys(entry as Record<string, unknown>);
	// V2 if any key is a known locale code and the value is an object (not a string)
	return keys.some((k) => KNOWN_LOCALES.has(k) && typeof (entry as Record<string, unknown>)[k] === "object");
}

function asV2Entry(raw: unknown): ProductUxMarketCopyV2 | null {
	if (!raw || typeof raw !== "object") return null;
	const r = raw as Record<string, unknown>;
	const title = typeof r.title === "string" ? r.title : "";
	const sd =
		typeof r.shortDescription === "string"
			? r.shortDescription
			: typeof r.short_description === "string"
				? r.short_description
				: "";
	const cn =
		typeof r.complianceNote === "string"
			? r.complianceNote
			: typeof r.compliance_note === "string"
				? r.compliance_note
				: "";
	if (!sd && !title) return null;
	return {
		...(typeof r.copyVersion === "string" ? { copyVersion: r.copyVersion } : {}),
		title,
		shortDescription: sd,
		complianceNote: cn,
		seoTitle:
			typeof r.seoTitle === "string" ? r.seoTitle : typeof r.seo_title === "string" ? r.seo_title : null,
		seoDescription:
			typeof r.seoDescription === "string"
				? r.seoDescription
				: typeof r.seo_description === "string"
					? r.seo_description
					: null,
		keyBenefits: Array.isArray(r.keyBenefits)
			? r.keyBenefits.filter((v): v is string => typeof v === "string")
			: [],
		usageContext: typeof r.usageContext === "string" ? r.usageContext : null,
		safetySummary: typeof r.safetySummary === "string" ? r.safetySummary : null,
		faq: Array.isArray(r.faq) ? asFaqItems(r.faq) : [],
		shippingNote: typeof r.shippingNote === "string" ? r.shippingNote : null,
	};
}

/** Parse V2 format: channel -> locale -> fields */
function asLocalizedMarketCopyMap(
	value: Record<string, unknown>,
): Record<string, Record<string, ProductUxMarketCopyV2>> {
	const result: Record<string, Record<string, ProductUxMarketCopyV2>> = {};
	for (const [channel, channelEntry] of Object.entries(value)) {
		if (!isV2ChannelEntry(channelEntry)) continue;
		const localeMap: Record<string, ProductUxMarketCopyV2> = {};
		for (const [locale, localeEntry] of Object.entries(channelEntry as Record<string, unknown>)) {
			if (!KNOWN_LOCALES.has(locale)) continue;
			const parsed = asV2Entry(localeEntry);
			if (parsed) localeMap[locale] = parsed;
		}
		if (Object.keys(localeMap).length > 0) result[channel] = localeMap;
	}
	return result;
}

/** Select V2 market copy by channel+locale. Returns null if no translation exists. */
function selectLocalizedMarketCopy(
	map: Record<string, Record<string, ProductUxMarketCopyV2>>,
	channel: string,
	locale: string,
): ProductUxMarketCopyV2 | null {
	return map[channel]?.[locale] ?? map.default?.[locale] ?? map.eu?.[locale] ?? null;
}

function metadataToMap(metadata: MetadataEntry[] | null | undefined): Record<string, string> {
	if (!metadata || metadata.length === 0) return {};
	return metadata.reduce<Record<string, string>>((acc, item) => {
		if (item?.key && item.value) acc[item.key] = item.value;
		return acc;
	}, {});
}

function selectMarketCopy(
	marketCopyByChannel: Record<string, ProductUxMarketCopy>,
	channel: string,
): ProductUxMarketCopy | null {
	return (
		marketCopyByChannel[channel] ??
		marketCopyByChannel.default ??
		marketCopyByChannel.eu ??
		Object.values(marketCopyByChannel)[0] ??
		null
	);
}

export function parseProductUxMetadata(
	metadata: MetadataEntry[] | null | undefined,
	channel: string,
	locale: string = "de",
): ProductUxModel {
	if (!metadata || metadata.length === 0) return emptyUxModel;

	const meta = metadataToMap(metadata);
	const keyBenefits = asStringArray(parseJsonArray(meta.ux_key_benefits_json));
	const coldChainPattern =
		/k(?:ue|[uü])hltransport|k(?:ue|[uü])hlversand|cold.?chain|cadena.*fr[ií]o|cha[iî]ne.*froid|catena.*freddo|koudeketen|łańcuch.*chłodniczy/i;
	const trustBadges = asStringArray(parseJsonArray(meta.ux_trust_badges_json)).filter(
		(badge) => !coldChainPattern.test(badge),
	);
	const faq = asFaqItems(parseJsonArray(meta.ux_faq_json));

	const rawMarketCopy = parseJsonObject(meta.ux_market_copy_json);
	const marketCopyByChannel = asMarketCopyMap(rawMarketCopy);

	// Try V2 (channel -> locale -> fields) first
	const localizedMap = asLocalizedMarketCopyMap(rawMarketCopy);
	const localizedMarketCopy = selectLocalizedMarketCopy(localizedMap, channel, locale);

	const hasCoA = meta.ux_has_coa === "true" || meta.ux_has_coa === "1";
	const labVerified = meta.ux_lab_verified === "true" || meta.ux_lab_verified === "1";

	return {
		shortDescription: meta.ux_short_description ?? null,
		longDescription: meta.ux_long_description ?? null,
		usageContext: meta.ux_usage_context ?? null,
		shippingNote: meta.ux_shipping_note ?? null,
		safetySummary: meta.ux_safety_summary ?? null,
		keyBenefits,
		trustBadges,
		hasCoA,
		labVerified,
		faq,
		marketCopyByChannel,
		selectedMarketCopy: selectMarketCopy(marketCopyByChannel, channel),
		localizedMarketCopy,
	};
}
